import { useCallback, useRef, useState } from 'react';
import { useDownloadFiles } from '@/talons';
import { getOnStorage } from '@/shared';
import { useDbFunctions } from '@/talons/dbFunctions/useDBFunctions';

interface SyncOnDemandOptions {
  requiredTables: string[];
  force?: boolean;
}

interface SyncOnDemandReturn {
  isSyncing: boolean;
  syncIfNeeded: (options: SyncOnDemandOptions) => Promise<boolean>;
  getLastSyncInfo: () => { lastSync: string | null; timeAgo: string };
}

/**
 * Hook para sincronização sob demanda - REUTILIZA A INFRAESTRUTURA EXISTENTE
 * 
 * Usado quando componentes específicos (como modal de clientes) precisam
 * garantir que os dados estão disponíveis antes de renderizar
 */
export const useSyncOnDemand = (): SyncOnDemandReturn => {
  const [isSyncing, setIsSyncing] = useState(false);
  const syncingRef = useRef(false);
  const { getOnDB } = useDbFunctions();
  const { getLinks } = useDownloadFiles();

  /**
   * Verifica se as tabelas necessárias estão disponíveis
   */
  const checkTablesAvailable = useCallback(async (requiredTables: string[]): Promise<boolean> => {
    try {
      for (const tableName of requiredTables) {
        const data = await getOnDB(tableName as any);
        if (!data || (Array.isArray(data) && data.length === 0)) {
          console.log(`useSyncOnDemand: Tabela ${tableName} não disponível`);
          return false;
        }
      }
      return true;
    } catch (error) {
      console.error('useSyncOnDemand: Erro verificando tabelas:', error);
      return false;
    }
  }, []);

  /**
   * Obtém informações sobre o último sync
   */
  const getLastSyncInfo = useCallback(() => {
    const lastSync = getOnStorage('last_data_sync');
    let timeAgo = 'nunca';
    
    if (lastSync) {
      const lastSyncTime = new Date(lastSync).getTime();
      const now = Date.now();
      const diffMinutes = Math.floor((now - lastSyncTime) / (1000 * 60));
      
      if (diffMinutes < 60) {
        timeAgo = `${diffMinutes} minuto(s) atrás`;
      } else {
        const diffHours = Math.floor(diffMinutes / 60);
        timeAgo = `${diffHours} hora(s) atrás`;
      }
    }
    
    return { lastSync, timeAgo };
  }, []);

  /**
   * Executa sincronização apenas se necessário
   */
  const syncIfNeeded = useCallback(async (options: SyncOnDemandOptions): Promise<boolean> => {
    const { requiredTables, force = false } = options;

    // Evita sincronizações concorrentes
    if (syncingRef.current) {
      console.log('useSyncOnDemand: Sincronização já em andamento - aguardando...');
      return false;
    }

    try {
      syncingRef.current = true;
      setIsSyncing(true);

      console.log(`useSyncOnDemand: Verificando necessidade de sync para tabelas: ${requiredTables.join(', ')}`);

      // 1. Se não é forçado, verifica se dados estão disponíveis
      if (!force) {
        const tablesAvailable = await checkTablesAvailable(requiredTables);
        if (tablesAvailable) {
          console.log('useSyncOnDemand: Tabelas já disponíveis - PULANDO sync');
          return true;
        }
      }

      // 2. Executa sincronização usando a infraestrutura existente
      console.log('useSyncOnDemand: Executando sincronização necessária...');
      const syncResult = await getLinks({ 
        isBackground: true
      });

      if (syncResult) {
        // 3. Atualiza timestamp
        const now = new Date().toISOString();
        localStorage.setItem('last_data_sync', now);
        
        console.log('useSyncOnDemand: Sincronização concluída com sucesso');
        
        // 4. Verifica novamente se as tabelas agora estão disponíveis
        const finalCheck = await checkTablesAvailable(requiredTables);
        if (!finalCheck) {
          console.warn('useSyncOnDemand: Tabelas ainda indisponíveis após sync');
        }
        
        return finalCheck;
      }

      console.error('useSyncOnDemand: Falha na sincronização');
      return false;

    } catch (error) {
      console.error('useSyncOnDemand: Erro durante sincronização sob demanda:', error);
      return false;
    } finally {
      syncingRef.current = false;
      setIsSyncing(false);
    }
  }, [getLinks, checkTablesAvailable]);

  return {
    isSyncing,
    syncIfNeeded,
    getLastSyncInfo
  };
};